import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { LoggingService } from '@app-core';
import { environment } from '@app-env';
import { Claim, ClaimUpdate } from '@app-models';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { ClaimSearch } from '../models/claim-search';

@Injectable({
    providedIn: 'root',
})
export class ClaimService {
    private readonly http = inject(HttpClient);
    private readonly claimSubject = new BehaviorSubject<Claim | null>(null);
    private isSubmissionSuccessful = false;

    constructor() {}

    // Observable to hold the current claim for child components of Claim.component
    getCurrentClaim(): Observable<Claim | null> {
        return this.claimSubject.asObservable();
    }

    clearCurrentClaim(): void {
        this.claimSubject.next(null);
    }

    setSubmissionSuccessful(success: boolean): void {
        this.isSubmissionSuccessful = success;
    }

    getSubmissionSuccessful(): boolean {
        return this.isSubmissionSuccessful;
    }

    // REST Get Claim by ID
    getClaim(guid: string): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/${guid}`;
        return this.http.get<Claim>(restURL).pipe(
            tap((claim) => {
                this.claimSubject.next(claim);
                LoggingService.log('ClaimService', 'getClaim', `Fetched claim with GUID: ${guid}`);
            }),
        );
    }

    getClaimByReferenceAndStudentLastName(referenceNumber: string, lastName: string): Observable<ClaimSearch> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim?referenceNumber=${encodeURIComponent(referenceNumber)}&lastName=${encodeURIComponent(lastName)}`;
        return this.http.get<ClaimSearch>(restURL).pipe(
            tap((claim) => {
                LoggingService.log('ClaimService', 'getClaimByReferenceAndStudentLastName', `Fetched claim with reference: ${referenceNumber} and lastName: ${lastName}`);
            }),
        );
    }

    // REST Update Claim PUT
    updateClaim(claimUpdate: ClaimUpdate, claimId: number): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/${claimId}/parent/response`;
        return this.http.put<Claim>(restURL, claimUpdate).pipe(
            tap((claim) => {
                this.claimSubject.next(claim);
            }),
        );
    }

    // REST Update Claim PUT - update email address for physician
    updateClaimPhysicianEmail(claimId: number, physicianEmail: string): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/${claimId}/parent/physician/email`;
        return this.http.put<Claim>(restURL, physicianEmail).pipe(
            tap((claim) => {
                this.claimSubject.next(claim);
                LoggingService.log('ClaimService', 'updateClaimPhysicianEmail', `Updated physician email for claim with ID: ${claimId}`);
            }),
        );
    }

    // REST Update Claim PUT - update email address for employer
    updateClaimEmployerEmail(claimId: number, employerEmail: string): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/${claimId}/parent/employer/email`;
        return this.http.put<Claim>(restURL, employerEmail).pipe(
            tap((claim) => {
                this.claimSubject.next(claim);
                LoggingService.log('ClaimService', 'updateClaimEmployerEmail', `Updated employer email for claim with ID: ${claimId}`);
            }),
        );
    }

    // PHYSICIAN FUNCTIONS
    // REST Get Claim by GUID
    getPhysicianClaim(guid: string): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/physician/${guid}`;
        return this.http.get<Claim>(restURL).pipe(
            tap((claim) => {
                this.claimSubject.next(claim);
                LoggingService.log('ClaimService', 'getPhysicianClaim', `Fetched claim with GUID: ${guid}`);
            }),
        );
    }

    // REST Update Claim PUT
    updatePhysicianClaim(claimUpdate: ClaimUpdate, claimId: number): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/${claimId}/physician/response`;
        return this.http.put<Claim>(restURL, claimUpdate).pipe(
            tap((claim) => {
                this.claimSubject.next(claim);
            }),
        );
    }

    // REST Get Claim by GUID
    getEmptyPhysicianForm(guid: string): Observable<Blob> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/physician/${guid}/form`;
        return this.http.get(restURL, { responseType: 'blob' }).pipe(
            tap(() => {
                LoggingService.log('ClaimService', 'getEmptyPhysicianForm', `Fetched claim with GUID: ${guid}`);
            }),
        );
    }
    // send Physician Email
    emailPhysician(guid: string): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/physician/${guid}/email`;
        return this.http.post<Claim>(restURL, null).pipe(
            tap((claim) => {
                LoggingService.log('ClaimService', 'emailPhysician', `Sent email for claim with GUID: ${guid}`);
            }),
        );
    }

    // upload physician document
    uploadPhysicianDocument(guid: string, formdata: FormData): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/physician/${guid}/documents`;
        return this.http.post<Claim>(restURL, formdata).pipe(
            tap((claim) => {
                LoggingService.log('ClaimService', 'uploadPhysicianDocument', `Uploaded document for claim with GUID: ${guid}`);
            }),
        );
    }

    // EMPLOYER FUNCTIONS
    // REST Get Claim by GUID
    getEmployerClaim(guid: string): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/employer/${guid}`;
        return this.http.get<Claim>(restURL).pipe(
            tap((claim) => {
                this.claimSubject.next(claim);
                LoggingService.log('ClaimService', 'getEmployerClaim', `Fetched claim with GUID: ${guid}`);
            }),
        );
    }

    // REST Update Claim PUT
    updateEmployerClaim(claimUpdate: ClaimUpdate, claimId: number): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/${claimId}/employer/response`;
        return this.http.put<Claim>(restURL, claimUpdate).pipe(
            tap((claim) => {
                this.claimSubject.next(claim);
            }),
        );
    }

    // REST Get Claim by GUID
    getEmptyEmployerForm(guid: string): Observable<Blob> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/employer/${guid}/form`;
        return this.http.get(restURL, { responseType: 'blob' }).pipe(
            tap(() => {
                LoggingService.log('ClaimService', 'getEmptyEmployerForm', `Fetched claim with GUID: ${guid}`);
            }),
        );
    }
    // send Employer Email
    emailEmployer(guid: string): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/employer/${guid}/email`;
        return this.http.post<Claim>(restURL, null).pipe(
            tap((claim) => {
                LoggingService.log('ClaimService', 'emailEmployer', `Sent email for claim with GUID: ${guid}`);
            }),
        );
    }

    // upload employer document
    uploadEmployerDocument(guid: string, formdata: FormData): Observable<Claim> {
        const restURL = `${environment.DewarClaimsRespondentREST}/claim/employer/${guid}/documents`;
        return this.http.post<Claim>(restURL, formdata).pipe(
            tap((claim) => {
                LoggingService.log('ClaimService', 'uploadEmployerDocument', `Uploaded document for claim with GUID: ${guid}`);
            }),
        );
    }
}
